import React, { useEffect, useState } from 'react';
import styles from './Kpis.container.module.scss';
import Select, { ValueType, components } from 'react-select';
import Button, {
  ButtonSize,
  ButtonType
} from '../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../shared/hooks/window-resizer/window-resizer.hook';
import SalesRevenueChart from '../charts/sales-revenue-chart';
import OccupancyRateChart from '../charts/occupancy-rate-chart';
import ADRChart from '../charts/ADR-chart';
import RevenuePerAvailableRoomChart from '../charts/revenue-per-available-room-chart';
import SourceOfBookingChart from '../charts/source-of-booking-chart';
import { ErrorInfo, isErrorInfo } from '../../shared/interfaces/error-info.interface';
import { searchInUnitTypes } from '../../shared/services/data/unit-type.data';
import { getModuleName } from '../shared/services/Kpis.service';
import type { UnitTypeUiModel } from '../../shared/ui-models/unit-type.ui-model';
import { getAllUnitTypeGroups } from '../../shared/services/data/lookups.data';
import { UserRole } from '../../shared/enums/user-role.enum';
import type { UnitTypeGroupUiModel } from '../../shared/ui-models/unit-type-group.ui-model';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core';
import TotalRevenueChart from '../charts/total-revenue-chart';
import { OccupancyAndAverageDailyRateDataProvider } from '../shared/services/OccupancyAndAverageDailyRate-provider';
import { connect } from 'react-redux';
import type { RootState } from '../../shared/redux/reducers';
import { UnauthorizedChart } from '../charts/unauthorized-chart/unauthorized-chart.component';
import type { KPIChartsSearch } from '../../shared/models/kpis.model';

interface KpisContainerComponentProps {
  userRole?: UserRole;
  userRoles?: UserRole[];
}

/**
 * function KpisContainer components
 */

const CustomTooltip = withStyles({
  tooltip: {
    fontSize: '1.5rem' // Adjust the font size as needed
  }
})(Tooltip);

const MultiValue = (props: any) => {
  const maxToShow = props.selectProps.maxToShow;
  const length = props.getValue().length;
  const shouldBadgeShow = length > maxToShow;
  const displayLength = length - maxToShow;

  // Create a tooltip title with the selected items
  const tooltipTitle = props
    .getValue()
    .map((item: any) => item.label)
    .join(', ');

  if (props.index < maxToShow) {
    return <components.MultiValue {...props} />;
  } else if (props.index === maxToShow) {
    return (
      <CustomTooltip title={tooltipTitle}>
        <div className="root">
          {shouldBadgeShow && `+ ${displayLength} item${length !== 1 ? 's' : ''} selected`}
        </div>
      </CustomTooltip>
    );
  } else {
    return null;
  }
};

const KpisContainer: React.FC<KpisContainerComponentProps> = ({ userRole, userRoles }) => {
  const isMobileView = useWindowResizer();
  const [unitTypeGroupOptions, setUnitTypeGroupOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [unitTypeOptions, setUnitTypeOptions] = useState<
    { value: string | undefined; label: string }[]
  >([]);
  const [searchPayload, setSearchPayload] = useState<KPIChartsSearch>({ query: {} });
  const [applyFilter, setApplyFilters] = useState<KPIChartsSearch>({});
  const [unit, setUnit] = useState<{ value: string; label: string }[] | null>(null);
  const [unitGroup, setUnitGroup] = useState<{ value: string; label: string }[] | null>(null);

  const getUnitTypes = () => {
    searchInUnitTypes(getModuleName()).then((response: UnitTypeUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setUnitTypeOptions(
          response?.map((unitType: any) => ({
            value: unitType.id,
            label: unitType.name || ''
          }))
        );
        setSearchPayload({
          query: {
            ...searchPayload.query,
            unitTypeIds: response?.map((unitType: any) => unitType.id)
          }
        });
        setApplyFilters({
          query: {
            ...searchPayload.query,
            unitTypeIds: response?.map((unitType: any) => unitType.id)
          }
        });
      }
    });
  };

  const getAllUnitTypeGroup = () => {
    getAllUnitTypeGroups(getModuleName()).then((response: UnitTypeGroupUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setUnitTypeGroupOptions(
          response.map((unitGroup) => ({
            value: unitGroup.id as string,
            label: unitGroup.name
          }))
        );
      }
    });
  };

  const isPermited = (permittedList: UserRole[]) => {
    return (
      permittedList.indexOf(userRole) > -1 ||
      permittedList.some((item) => userRoles?.includes(item))
    );
  };

  useEffect(() => {
    getUnitTypes();
    getAllUnitTypeGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles['kpis']}>
      <div className={styles['kpis-filters-container']}>
        <div className={styles['kpis-filters-container__filters']}>
          <div className={styles['kpis-filters-container__search']}>
            <div className={styles['kpis-filters-container__search__form-field']}>
              <Select
                value={unitGroup}
                options={unitTypeGroupOptions}
                isMulti
                isSearchable={true}
                maxToShow={5}
                placeholder="Unit type group"
                className={styles['kpis-filters-container__search__form-field__select']}
                onChange={(value: ValueType<{ value: string; label: string }[], false>) => {
                  setUnitGroup(value ? value : null);
                  setSearchPayload({
                    query: {
                      ...searchPayload.query,
                      unitTypeGroupIds: value?.length
                        ? value.map((option) => option.value)
                        : undefined
                    }
                  });
                }}
                components={{ MultiValue }}
              />
            </div>
            <div className={styles['kpis-filters-container__search__form-field']}>
              <Select
                value={unit}
                options={unitTypeOptions}
                isMulti
                isSearchable={true}
                maxToShow={3}
                placeholder="Unit type"
                className={styles['kpis-filters-container__search__form-field__select']}
                onChange={(value: ValueType<{ value: string; label: string }[], false>) => {
                  setUnit(value ? value : null);
                  setSearchPayload({
                    query: {
                      ...searchPayload.query,
                      unitTypeIds: value?.length
                        ? value.map((option) => Number(option.value))
                        : undefined
                    }
                  });
                }}
                components={{ MultiValue }}
              />
            </div>
            <div className={styles['kpis-filters-container__search__actions']}>
              <Button
                className={styles['kpis-filters-container__search__button']}
                onClick={() => {
                  setApplyFilters(searchPayload);
                }}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                Apply
              </Button>
              <Button
                className={styles['kpis-filters-container__search__button']}
                type={ButtonType.gray}
                onClick={() => {
                  setApplyFilters({
                    query: {
                      unitTypeIds: unitTypeOptions?.map((unitType) => Number(unitType.value))
                    }
                  });
                  setSearchPayload({
                    query: {
                      unitTypeIds: unitTypeOptions?.map((unitType) => Number(unitType.value))
                    }
                  });
                  setUnit(null);
                  setUnitGroup(null);
                }}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                Reset
              </Button>
            </div>
          </div>
        </div>
      </div>
      <OccupancyAndAverageDailyRateDataProvider monthsCount={11} payload={applyFilter}>
        <div>
          {isPermited([UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.PARTNER_MANAGER]) ? (
            <TotalRevenueChart query={applyFilter} />
          ) : (
            <UnauthorizedChart chartName="Total revenue" />
          )}
        </div>
        <div className={styles['kpis__charts']}>
          {isPermited([UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.PARTNER_MANAGER]) ? (
            <OccupancyRateChart query={applyFilter} />
          ) : (
            <UnauthorizedChart chartName="Occupancy Rate" />
          )}

          {isPermited([UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.PARTNER_MANAGER]) ? (
            <ADRChart query={applyFilter} />
          ) : (
            <UnauthorizedChart chartName="ADR" />
          )}
        </div>
        <div className={styles['kpis__charts']}>
          {isPermited([UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.PARTNER_MANAGER]) ? (
            <RevenuePerAvailableRoomChart query={applyFilter} />
          ) : (
            <UnauthorizedChart chartName="Revenue Per Available Room" />
          )}
          {isPermited([UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.PARTNER_MANAGER]) ? (
            <SalesRevenueChart query={applyFilter} />
          ) : (
            <UnauthorizedChart chartName="Sales Revenue" />
          )}
        </div>
        <div className={styles['kpis__charts']}>
          {isPermited([UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.PARTNER_MANAGER]) ? (
            <SourceOfBookingChart query={applyFilter} />
          ) : (
            <UnauthorizedChart chartName="Source Of Booking" />
          )}
        </div>
      </OccupancyAndAverageDailyRateDataProvider>
    </div>
  );
};

/**
 * function to to map redux state to component props
 * @param state root state or redux
 */
function mapStateToProps(state: RootState) {
  return {
    isAuthenticated: !!state.auth.accessToken,
    userRole: state.auth.role,
    userRoles: state.auth.roles,
    userEmail: state.auth.email,
    userNotifications: state.auth.userNotifications
  };
}

export default connect(mapStateToProps)(KpisContainer);
